import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CircleSlash,
  HelpCircle,
  Loader2,
} from 'lucide-react';
import { Badge } from '@roger/ui-components';
import type { EligibilityCheckResponse } from '../../services/api';
import { formatDateInAgencyZone } from '../../utils/dateUtils';
import { freshnessOf } from './eligibilityStatus';

interface EligibilityCheckSummaryProps {
  /** The most recent check for this coverage, or null when none has run. */
  check: EligibilityCheckResponse | null;
  /**
   * The check is not known yet. Distinct from null: drawing "Not verified"
   * while the fetch is still out invites a second, separately billed 270.
   */
  loading?: boolean;
  /**
   * The lookup failed, so nothing is known either way. Also distinct from
   * null: "Not verified" is a confident answer, and the confident answer to a
   * failed request is the one that gets a duplicate 270 run.
   */
  failed?: boolean;
}

/**
 * One line saying whether coverage was verified, and how long ago (EMR-1655).
 *
 * This is the shape billing asked for in EMR-305 ("elig. last verified at: XXX"),
 * kept separate from the history list so billing can reuse it as a gate readout
 * without inheriting a browser.
 */
export const EligibilityCheckSummary: React.FC<
  EligibilityCheckSummaryProps
> = ({ check, loading = false, failed = false }) => {
  const freshness = freshnessOf(check);

  if (failed) {
    return (
      <span className='inline-flex items-center gap-1.5 text-xs text-muted-foreground'>
        <HelpCircle className='h-3.5 w-3.5' />
        Status unavailable
      </span>
    );
  }

  if (loading) {
    return (
      <span className='inline-flex items-center gap-1.5 text-xs text-muted-foreground'>
        <Loader2 className='h-3.5 w-3.5 animate-spin' />
        Checking…
      </span>
    );
  }

  if (!check || freshness === 'none') {
    return (
      <span className='inline-flex items-center gap-1.5 text-xs text-muted-foreground'>
        <CircleSlash className='h-3.5 w-3.5' />
        Not verified
      </span>
    );
  }

  if (freshness === 'pending') {
    return (
      <span className='inline-flex items-center gap-1.5 text-xs text-muted-foreground'>
        <Loader2 className='h-3.5 w-3.5 animate-spin' />
        Check in progress
      </span>
    );
  }

  if (freshness === 'failed') {
    return (
      <span className='inline-flex items-center gap-1.5 text-xs text-destructive'>
        <AlertTriangle className='h-3.5 w-3.5' />
        Last check failed
      </span>
    );
  }

  if (freshness === 'inactive') {
    return (
      <span className='inline-flex items-center gap-1.5 text-xs text-destructive'>
        <AlertTriangle className='h-3.5 w-3.5' />
        Coverage inactive
      </span>
    );
  }

  // formatDateInAgencyZone reads the timestamp as UTC and renders the agency's
  // calendar date. Slicing the ISO string instead would show the UTC day, which
  // is already tomorrow for anything run after a US afternoon.
  const on = check.response_date
    ? formatDateInAgencyZone(check.response_date)
    : null;

  return (
    <span className='inline-flex items-center gap-1.5 text-xs text-muted-foreground'>
      <CheckCircle2 className='h-3.5 w-3.5 text-green-600' />
      Verified {on}
      {freshness === 'stale' && (
        <Badge variant='outline' className='whitespace-nowrap'>
          Out of date
        </Badge>
      )}
    </span>
  );
};
